import { type NavigateFunction } from 'react-router'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { isElectron } from '~/utils/common'
import {
  getLocalStorageObjectKey,
  setLocalStorageObjectKey,
} from '~/utils/localStorageClient'
import { PresenceState } from '~/utils/xml'

import { IpcRendererListener } from '@mangovoice/types'

const events = {
  deepLink: 'deep-link',
  electronLog: 'electron-log',
  openSettings: 'open-settings',
  openMessages: 'open-messages',
  popupExperiment: 'popup-experiment',
  setPopupDimensions: 'set-popup-dimensions',
  getPopupDimensions: 'get-popup-dimensions',
  updateMenusAfterAuth: 'update-menus-after-auth',
  answerCall: 'popup-answer-call',
  answerCallWithHold: 'popup-answer-call-with-hold',
  answerCallWithEnd: 'popup-answer-call-with-end',
  rejectCall: 'popup-reject-call',
  parkCall: 'popup-park-call',
}

const localStorageKeys = {
  experiments: 'experiments',
  popupSettings: 'popupSettings',
  popupDimensions: 'windowDimensions',
}

type Dimensions = {
  height: number
  width: number
}

const defaultDimensions: Dimensions = {
  width: 275,
  height: 200,
}

const deepLinkListener = (url: string, navigate: NavigateFunction) => {
  if (!url) {
    console.warn('Received empty deep link URL')
    return
  }

  try {
    if (url.startsWith('tel:')) {
      // Extract the phone number from the URL
      const phoneNumber = url.replace(/^tel:\/?\/?/, '')
      console.log(`Extracted phone number: ${phoneNumber}`)

      // Navigate to the webphone page with the number as a query parameter
      navigate(`${ApplicationRoutes.WEB_PHONE}?number=${phoneNumber}`)
      return
    }

    let path = ''
    let id = ''
    let section = ''
    let newUrl: string = ''

    const urlWithoutProtocol = url.replace(/^[a-zA-Z]+:\/\//, '')

    const searchSegment = urlWithoutProtocol.split('?')[1]
    const urlSegments = urlWithoutProtocol
      .split('?')[0]
      .split('/')
      .filter(Boolean)

    if (urlSegments.length > 0) {
      path = `/${urlSegments[0].toLowerCase()}`

      if (urlSegments.length > 1) {
        id = urlSegments[1]
      }

      if (urlSegments.length > 2) {
        section = urlSegments[2]
      }
    } else {
      path = '/'
    }

    const routeMap: { [key: string]: ApplicationRoutes } = {
      '/': ApplicationRoutes.HOME,
      '/login': ApplicationRoutes.LOGIN,
      '/calls': ApplicationRoutes.CALLS,
      '/messaging': ApplicationRoutes.MESSAGING,
      '/voicemails': ApplicationRoutes.VOICEMAIL,
      '/faxes': ApplicationRoutes.FAX,
      '/analytics': ApplicationRoutes.CALL_ANALYTICS,
      '/web-phone': ApplicationRoutes.WEB_PHONE,
      '/settings': ApplicationRoutes.SETTINGS,
      '/contacts': ApplicationRoutes.CONTACTS,
      '/dashboards': ApplicationRoutes.DASHBOARDS,
    }

    const supportedPaths = Object.keys(routeMap)

    if (!supportedPaths.includes(path)) {
      console.warn(`Unsupported deep link path: ${path}`)
      navigate(ApplicationRoutes.HOME)
      return
    }

    if (id) {
      if (/^[a-zA-Z0-9-_]+$/.test(id)) {
        if (
          path === '/voicemails' ||
          path === '/faxes' ||
          path === '/calls' ||
          path === '/contacts' ||
          path === '/dashboards'
        ) {
          newUrl = `${path}/${id}`
        } else if (path === '/settings') {
          newUrl = `${path}/${id}${section.length > 0 ? `#${section}` : ''}`
        }
      } else {
        console.warn(`Unsupported deep link ID: ${id}`)
        navigate(ApplicationRoutes.HOME)
        return
      }
    } else {
      newUrl = path
    }

    if (searchSegment) {
      newUrl += `?${searchSegment}`
    }

    navigate(newUrl)
  } catch (error) {
    console.error('Error processing deep link URL:', error)
    navigate(ApplicationRoutes.HOME)
  }
}

/**
 * Listens for an event that triggers navigation to the messaging page.
 *
 * @param {function} navigate - The navigation function used to change the route
 * to the messaging page.
 */
const gotoMessagingListener = (navigate: any) => {
  navigate(ApplicationRoutes.MESSAGING)
}

/**
 * Navigates to the settings page when triggered by a hotkey event.
 *
 * @param navigate - The navigation function used to change the route
 * to the settings page.
 */
const settingsHotkeyListener = (navigate: any) => {
  navigate(ApplicationRoutes.SETTINGS)
}

/**
 * Listens for the event to send the popup window's experiments data to the
 * Electron main process.
 *
 * The data is retrieved from local storage using the key
 * `localStorageKeys.experiments`. If the key is not present, an empty object is
 * sent instead.
 */
const experimentsListener = () => {
  try {
    window.electron?.send(
      events.popupExperiment,
      window.localStorage.getItem(localStorageKeys.experiments) || '{}',
    )
  } catch (e) {
    console.error(e)
    return
  }
}

/**
 * Listens for the event to get the popup window's dimensions and sends the
 * dimensions to the Electron main process.
 */
const popupGetDimensionsListener = () => {
  try {
    const dimensions = getLocalStorageObjectKey({
      storageKey: localStorageKeys.popupSettings,
      keyInObject: localStorageKeys.popupDimensions,
      initialValue: defaultDimensions,
    })

    window.electron?.send(events.getPopupDimensions, dimensions)
  } catch (e) {
    console.error(e)
    return
  }
}

/**
 * Updates the local storage with the dimensions of the popup window.
 *
 * @param {number} args.height - The height of the popup window.
 * @param {number} args.width - The width of the popup window.
 */
const popupSetDimensionsListener = (args: {
  height: number
  width: number
}) => {
  setLocalStorageObjectKey<Dimensions>(
    {
      storageKey: localStorageKeys.popupSettings,
      keyInObject: localStorageKeys.popupDimensions,
      initialValue: defaultDimensions,
    },
    args,
  )
}

/**
 * Sends a message to the Electron main process to update the application's menus.
 *
 * @param {boolean} args.enableAuthedMenuItems - If true, includes menu items that are only enabled when the user is authenticated.
 */
const updateMenus = (args: { enableAuthedMenuItems?: boolean } = {}) => {
  const { enableAuthedMenuItems = false } = args

  try {
    window.electron?.send(events.updateMenusAfterAuth, {
      enableAuthedMenuItems,
    })
  } catch (e) {
    console.error(e)
    return
  }
}

const handleAnswerPhoneCall = (args: { answerCallFn: () => void }) => {
  args.answerCallFn()
}

const handleRejectPhoneCall = (args: { rejectCallFn: () => void }) => {
  args.rejectCallFn()
}

const handleAnswerCallWithEnd = (args: { answerCallWithEndFn: () => void }) => {
  args.answerCallWithEndFn()
}

const handleAnswerCallWithHold = (args: { answerCallHoldFn: () => void }) => {
  args.answerCallHoldFn()
}

const handleParkCall = (args: {
  handleParkFn: (parkingLot: TrackedParkingLot) => void
  parkingLot: TrackedParkingLot
}) => {
  args.handleParkFn(args.parkingLot)
}

type HandlePhoneCallProps = {
  answerCall: () => void
  rejectCall: () => void
  answerCallWithEnd: () => void
  answerCallWithHold: () => void
  parkCall: (parkingLot: TrackedParkingLot) => void
  parkingLots: TrackedParkingLot[]
}

/**
 * Provides methods to interact with the Electron main process.
 *
 * This hook listens to the following events:
 *
 * - `deepLink`: Listens to the event to handle deep links.
 * - `openSettings`: Listens to the event to open the settings window.
 * - `popupExperiment`: Listens to the event to retrieve the popup window's
 *   experiments.
 * - `openMessages`: Listens to the event to open the messaging window.
 * - `getPopupDimensions`: Listens to the event to retrieve the popup window's
 *   dimensions.
 * - `setPopupDimensions`: Listens to the event to update the popup window's
 *   dimensions.
 *
 * The hook also provides methods to remove the listeners and to recreate the
 * menu bar.
 *
 * @returns An object with methods to interact with the Electron main process.
 */
const useElectronComms = () => {
  const [parkingLot] = useSharedVariable<TrackedParkingLot>('ParkingLots', {
    id: 0,
    label: '',
    defaultLabel: '',
    targetNumber: '',
    presence: PresenceState.Unknown,
  } as TrackedParkingLot)

  const removeListener = (
    channelName: string,
    channelListener: IpcRendererListener,
  ) => {
    if (
      window.electron &&
      typeof window.electron.removeListener === 'function'
    ) {
      window.electron.removeListener(channelName, channelListener)
    }
  }

  const initDeepLinkListener = (navigate: NavigateFunction) => {
    const deepLinkFn = (url: string) => deepLinkListener(url, navigate)

    window.electron?.receive(events.deepLink, deepLinkFn)
    return () => removeListener(events.deepLink, deepLinkFn)
  }

  const initSettingsHotkeyListener = (navigate: NavigateFunction) => {
    const settingsHotKeyFn = () => settingsHotkeyListener(navigate)

    window.electron?.receive(events.openSettings, settingsHotKeyFn)
    return () => removeListener(events.openSettings, settingsHotKeyFn)
  }

  const initGetExperimentsListener = () => {
    window.electron?.receive(events.popupExperiment, experimentsListener)
    return () => removeListener(events.popupExperiment, experimentsListener)
  }

  const initGoToMessagingListener = (navigate: NavigateFunction) => {
    const gotoMessagingFn = () => gotoMessagingListener(navigate)

    window.electron?.receive(events.openMessages, gotoMessagingFn)
    return () => removeListener(events.openMessages, gotoMessagingFn)
  }

  const initGetPopupWindowDimensionsListener = () => {
    window.electron?.receive(
      events.getPopupDimensions,
      popupGetDimensionsListener,
    )

    return () =>
      removeListener(events.getPopupDimensions, popupGetDimensionsListener)
  }

  const initSetPopupWindowDimensionsListener = () => {
    window.electron?.receive(
      events.setPopupDimensions,
      popupSetDimensionsListener,
    )

    return () =>
      removeListener(events.setPopupDimensions, popupSetDimensionsListener)
  }

  const initHandlePhoneCallListener = ({
    answerCall,
    rejectCall,
    answerCallWithEnd,
    answerCallWithHold,
    parkCall,
  }: HandlePhoneCallProps) => {
    if (!isElectron) {
      return () => {}
    }

    const handleAnswer = () =>
      handleAnswerPhoneCall({ answerCallFn: answerCall })

    const handleReject = () =>
      handleRejectPhoneCall({ rejectCallFn: rejectCall })

    const handleAnswerEnd = () =>
      handleAnswerCallWithEnd({
        answerCallWithEndFn: answerCallWithEnd,
      })

    const handleAnswerHold = () =>
      handleAnswerCallWithHold({
        answerCallHoldFn: answerCallWithHold,
      })

    const handlePark = () =>
      handleParkCall({
        handleParkFn: parkCall,
        parkingLot: parkingLot!,
      })

    window.electron?.receive(events.answerCall, handleAnswer)
    window.electron?.receive(events.rejectCall, handleReject)
    window.electron?.receive(events.answerCallWithEnd, handleAnswerEnd)
    window.electron?.receive(events.answerCallWithHold, handleAnswerHold)
    window.electron?.receive(events.parkCall, handlePark)

    return () => {
      removeListener(events.answerCall, handleAnswer)
      removeListener(events.rejectCall, handleReject)
      removeListener(events.answerCallWithEnd, handleAnswerEnd)
      removeListener(events.answerCallWithHold, handleAnswerHold)
      removeListener(events.parkCall, handlePark)
    }
  }

  return {
    initDeepLinkListener,
    initSettingsHotkeyListener,
    initGetExperimentsListener,
    initGoToMessagingListener,
    initGetPopupWindowDimensionsListener,
    initSetPopupWindowDimensionsListener,
    updateMenus,
    initHandlePhoneCallListener: ({
      answerCall,
      rejectCall,
      answerCallWithEnd,
      answerCallWithHold,
      parkCall,
      parkingLots,
    }: HandlePhoneCallProps) =>
      initHandlePhoneCallListener({
        answerCall,
        rejectCall,
        answerCallWithEnd,
        answerCallWithHold,
        parkCall,
        parkingLots,
      }),
  }
}

export default useElectronComms
